import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, NavigationType, useNavigate, useNavigationType } from 'react-router';
import { Icon, Panel, Stat, EmptyState, Button, Tag, useErrorDialog } from '@dayon/design-system';
import { formatPesos, formatPesosCompact, formatCount } from '@dayon/money';
import type { ActionQueue, DashboardResponse, DateRange } from '@dayon/contracts';
import { gamingDate } from '@dayon/contracts';
import type { Permission } from '@dayon/rbac';
import { api, ApiRequestError } from '../../lib/api.ts';
import { LiveOps } from './LiveOps.tsx';
import { QueuePill } from './QueuePill.tsx';
import { queueOpens, queueWithheld, type WorkKind } from './queue-links.ts';
/* The custom window is defined once, beside the BI screen, and imported here —
   see the block comment on `customWindow` for why it lives over there and why
   there must not be a second copy of the boundary rule. */
import {
  PeriodBI,
  CustomRangeFields,
  customWindow,
  gamingDayName,
  lastIncludedDayName,
} from './PeriodBI.tsx';
import { useSession, type Brand } from '../../lib/session.tsx';
import { allBrandsPhrase } from '../../lib/brand-words.ts';
import { formatWait } from '../../lib/wait.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { LicenceWarnings } from './LicenceWarnings.tsx';
import { AreaChart } from './charts.tsx';
import './dashboard.css';

const RANGES: { key: DateRange; label: string }[] = [
  { key: 'TODAY', label: 'Today' },
  { key: 'SEVEN_DAYS', label: '7D' },
  { key: 'THIRTY_DAYS', label: '30D' },
  { key: 'QTD', label: 'QTD' },
  /* CUSTOM has been in `dashboardQuery` and in `resolveWindow` since the module
     was written; until this option existed no screen could ask for it. */
  { key: 'CUSTOM', label: 'Custom' },
];


/** "▲ 12.4%" / "▼ 1.8%" / "flat". Null delta means there is no comparison. */
function Delta({ bp }: { bp: number | null }) {
  if (bp === null) return <span className="text-subtle">no prior period</span>;
  if (bp === 0) return <span>flat</span>;
  const up = bp > 0;
  return (
    <span className={up ? 'stat-delta up' : 'stat-delta'}>
      {up ? '▲' : '▼'} {Math.abs(bp / 100).toFixed(1)}% vs prior
    </span>
  );
}

/**
 * What stands in for every figure the range control governs while a custom
 * window is half-chosen.
 *
 * `queueOnScreen` is false in the branch where the read behind the queue band
 * failed or never happened, so the copy does not promise something that is
 * still on screen when it is not.
 */
function NoWindowYet({ queueOnScreen }: { queueOnScreen: boolean }) {
  return (
    <Panel>
      <EmptyState icon="calendar-blank" title="Choose the custom window">
        Nothing is read until a first and a last day are both set. The figures that were here
        belonged to the previous range, so they have been taken down rather than left under a Custom
        button as though they answered it.
        {queueOnScreen
          ? ' The action queue and any licence warning stay: neither of those is measured over the range.'
          : ''}
      </EmptyState>
    </Panel>
  );
}

/**
 * Dashboard C — the split layout the handoff flags as primary.
 *
 * The range control drives every widget, and the comparison series is always
 * the immediately prior period of equal length. The brand filter comes from the
 * session, not from this screen, because it persists across modules.
 */
function SplitDashboard() {
  const { brandId, brands, allowed, session } = useSession();
  const { showError } = useErrorDialog();
  const navigate = useNavigate();

  const [range, setRange] = useState<DateRange>('THIRTY_DAYS');
  /* The two custom dates as typed. Kept across a switch to another range so
     coming back to Custom does not lose the window that was chosen. */
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState<DashboardResponse | null>(null);
  /*
   * WHICH RANGE THE FIGURES ON SCREEN CAME FROM.
   *
   * Not the one the control is set to. Between a click and its response those
   * differ, and the line under the heading has to name the window the numbers
   * belong to rather than the one that was just asked for.
   */
  const [shownRange, setShownRange] = useState<DateRange>('THIRTY_DAYS');
  const [loading, setLoading] = useState(true);
  /*
   * Whether the last read FAILED, as distinct from having returned nothing.
   *
   * Every empty branch on this screen is a claim about the business: the trend
   * panel says "a real zero, not a missing feed", a queue pill reading 0 says
   * the queue is clear, an empty top-games table says nothing was played. All
   * of those are only true once the read has come back. After a failure the
   * honest answer is "unknown", so the figures are dropped and this flag draws
   * the failure in their place — the operator dismisses the modal and must not
   * be left reading a quiet dashboard as a quiet day.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  /* Bumped by Try again. The range control is not on screen after a failure,
     so without this the only way back is a page reload. */
  const [attempt, setAttempt] = useState(0);

  /*
   * The custom window, read off the two date inputs.
   *
   * Null unless CUSTOM is selected AND both dates are readable and in order —
   * which is what stops an incomplete pair from ever reaching the API, and what
   * keeps the effect from re-firing when dates are typed under a range that
   * does not use them.
   */
  const custom = customWindow(customFrom, customTo);
  const requestedFrom = range === 'CUSTOM' ? custom.from : null;
  const requestedTo = range === 'CUSTOM' ? custom.to : null;
  const awaitingCustom = range === 'CUSTOM' && (requestedFrom === null || requestedTo === null);

  useEffect(() => {
    /*
     * An unusable custom pair asks for NOTHING, and says so on screen.
     *
     * `data` is deliberately kept: the action queue and the licence warnings in
     * it are not windowed by the range control — they are live counts and a
     * regulator's date — so taking them down because somebody opened a date
     * picker would hide work and hide an expiry. Everything the range DOES
     * govern is withheld by `awaitingCustom` in the render below.
     */
    if (range === 'CUSTOM' && (requestedFrom === null || requestedTo === null)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const query = new URLSearchParams({ range });
    if (requestedFrom && requestedTo) {
      query.set('from', requestedFrom);
      query.set('to', requestedTo);
    }
    if (brandId) query.set('brandId', brandId);

    api
      .get<DashboardResponse>(`/api/dashboard?${query}`)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setShownRange(range);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // The previous window's figures go with the failure. Leaving them up
        // puts last month's GGR under this month's radio button, and a stale
        // number under a failure notice is read as a current one.
        setData(null);
        setLoadFailed(true);
        showError({
          title: 'Could not load the dashboard',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The figures could not be fetched. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [range, requestedFrom, requestedTo, brandId, showError, attempt]);

  /**
   * The heading, the range control and — when Custom is chosen — the two date
   * inputs.
   *
   * Lifted out of the main return because three states need it: the figures, a
   * failed read, and a custom window that has not been finished. A range
   * control that vanishes in two of them leaves the operator with no way back
   * to 30D and no way to correct a date except reloading the page.
   */
  const header = (caption: ReactNode) => (
    <>
      <div className="row">
        <div>
          <h3 style={{ margin: 0 }}>Performance</h3>
          <div className="text-subtle" style={{ fontSize: '12.5px' }}>
            {caption}
          </div>
        </div>
        <div className="seg header-spacer dashboard-range">
          {RANGES.map((r) => (
            <label className="seg-opt" key={r.key}>
              <input
                type="radio"
                name="range"
                checked={range === r.key}
                onChange={() => setRange(r.key)}
              />
              {r.label}
            </label>
          ))}
        </div>
      </div>
      {range === 'CUSTOM' && (
        <CustomRangeFields
          from={customFrom}
          to={customTo}
          onFrom={setCustomFrom}
          onTo={setCustomTo}
          errors={custom.errors}
          window={{ from: custom.from, to: custom.to }}
        />
      )}
    </>
  );

  if (loading && !data) {
    return (
      <div className="stat-grid">
        {Array.from({ length: 4 }, (_, i) => (
          <div className="stat" key={i}>
            <div className="skeleton" style={{ height: '11px', width: '60%' }}>
              &nbsp;
            </div>
            <div className="skeleton" style={{ height: '25px', width: '80%' }}>
              &nbsp;
            </div>
          </div>
        ))}
      </div>
    );
  }
  /*
   * Nothing was read. The modal has already said so and been dismissed; this is
   * what is left behind, and it must not be a blank screen that reads as a
   * finished, empty one. Because the catch clears `data`, everything past this
   * point is drawn from a read that SUCCEEDED — which is what keeps the trend
   * panel's "real zero" copy below honest.
   */
  if (!data) {
    /* A custom window nobody has finished choosing, with nothing behind it —
       the read that would have filled the queue band failed or has not
       happened. Nothing was requested, so nothing is loading and nothing
       failed. */
    if (awaitingCustom) {
      return (
        <div className="shell-body dashboard-body">
          {header('No window is being shown — pick both dates below.')}
          <NoWindowYet queueOnScreen={false} />
        </div>
      );
    }
    if (!loadFailed) return null;
    return (
      <div className="shell-body dashboard-body">
        {header('Nothing is being shown — the last read failed.')}
        <Panel>
          <EmptyState
            icon="warning-circle"
            title="The dashboard could not be read"
            action={<Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>}
          >
            The dashboard could not be fetched, so the action queue, the KPI band and the GGR
            series are not drawn at all. This is not a quiet period: no figure came back,
            and a zero printed here would have been invented. The range control is still above, so
            another window can be asked for without reloading.
          </EmptyState>
        </Panel>
      </div>
    );
  }

  const { summary, queue, trend, brandMix, topGames, topPlayers, licences, unavailable } = data;

  /*
   * WHICH WINDOW THE FIGURES BELOW ARE FOR, in the words of the payload that
   * carried them — never of the control, which may already have moved on.
   *
   * The custom branch prints Manila gaming days and says so. It has to: the
   * operator picked calendar dates in Manila, `summary.range.to` is the
   * boundary that CLOSES the window rather than a day inside it, and printing
   * either of those in the reader's own zone would name a day the figures do
   * not cover.
   */
  const shownWindow =
    shownRange === 'CUSTOM' ? (
      <>
        {gamingDayName(summary.range.from)} – {lastIncludedDayName(summary.range.to)} · custom
        window on Asia/Manila gaming days · vs the prior period of equal length
      </>
    ) : (
      <>
        {gamingDate(summary.range.from)} –{' '}
        {gamingDate(summary.range.to)} · {rangeLabel(shownRange)} · vs the
        prior period of equal length
      </>
    );

  return (
    <>
      <ActionQueueBand
        queue={queue}
        brandId={brandId}
        brands={brands}
        allowed={allowed}
        role={session?.role ?? 'your role'}
        onOpen={(to) => navigate(to)}
      />

      <div className="shell-body dashboard-body">
        {/* The 60-day licence warning. Nothing at all when every licence in
            scope is comfortably in date, which is the usual case. */}
        <LicenceWarnings licences={licences} canOpenSettings={allowed('platform.view')} />

        {/* Range control drives every widget below. */}
        {header(awaitingCustom ? 'No window is being shown — pick both dates below.' : shownWindow)}

        {awaitingCustom ? (
          <NoWindowYet queueOnScreen />
        ) : (
          <>
            {/* The asymmetric split: chart left, KPIs and brand mix right. */}
            <div className="split">
              <Panel className="dashboard-chart">
                <div className="row">
                  <div>
                    {/* The window the FIGURE is for, not the button that is lit. */}
                    <div className="stat-label">GGR · {rangeLabel(shownRange)}</div>
                    <div className="stat-value lg">
                      {formatPesosCompact(BigInt(summary.ggr.value))}
                    </div>
                  </div>
                  <span className="header-spacer">
                    <Delta bp={summary.ggr.deltaBp} />
                  </span>
                </div>
                {trend.length > 0 ? (
                  <AreaChart
                    points={trend.map((t) => Number(BigInt(t.ggr) / 100n))}
                    labels={trend.map((t) => t.day)}
                  />
                ) : (
                  <EmptyState icon="chart-line" title="No activity in this range">
                    Nothing was staked between these dates, so there is no series to draw. This is a
                    real zero, not a missing feed.
                  </EmptyState>
                )}
              </Panel>

              <div className="stack">
                <div className="dashboard-kpis">
                  <Stat
                    label="Deposits"
                    value={formatPesosCompact(BigInt(summary.deposits.value))}
                    delta={<Delta bp={summary.deposits.deltaBp} />}
                    deltaUp={(summary.deposits.deltaBp ?? 0) > 0}
                  />
                  <Stat
                    label="Withdrawals"
                    value={formatPesosCompact(BigInt(summary.withdrawals.value))}
                    delta={<Delta bp={summary.withdrawals.deltaBp} />}
                  />
                  <Stat
                    label="Actives"
                    value={formatCount(summary.activePlayers.value)}
                    delta={<Delta bp={summary.activePlayers.deltaBp} />}
                    deltaUp={(summary.activePlayers.deltaBp ?? 0) > 0}
                  />
                  <Stat
                    label="Hold"
                    value={summary.holdBp === null ? '—' : `${(summary.holdBp / 100).toFixed(1)}%`}
                    delta={
                      summary.holdBp === null ? (
                        <span className="text-subtle">nothing staked</span>
                      ) : (
                        <span>ARPU {formatPesos(BigInt(summary.arpu))}</span>
                      )
                    }
                  />
                </div>

                <Panel title="Brand mix · GGR">
                  {brandMix.map((b) => (
                    <div key={b.brandId} style={{ marginBottom: 'var(--space-3)' }}>
                      <div className="meter-row">
                        <span>{b.brandName}</span>
                        <span className="tabular text-muted">
                          {formatPesosCompact(BigInt(b.ggr))} · {(b.shareBp / 100).toFixed(0)}%
                        </span>
                      </div>
                      <div
                        className="meter"
                        role="img"
                        aria-label={`${b.brandName}: ${(b.shareBp / 100).toFixed(0)} percent of GGR`}
                      >
                        <div className="meter-fill" style={{ width: `${b.shareBp / 100}%` }} />
                      </div>
                    </div>
                  ))}
                </Panel>
              </div>
            </div>

            {/* Top games and top players, side by side. */}
            <div className="halves">
              <Panel
                title="Top games"
                actions={
                  <Button variant="ghost" onClick={() => navigate('/games')}>
                    All games
                  </Button>
                }
              >
                <div className="table-scroll">
                  <table className="table responsive">
                    <caption className="sr-only">Top games by gross gaming revenue</caption>
                    <thead>
                      <tr>
                        <th scope="col">Game</th>
                        <th scope="col">Provider</th>
                        <th scope="col" className="num">
                          Hold
                        </th>
                        <th scope="col" className="num">
                          GGR
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topGames.map((g) => (
                        <tr key={g.gameId}>
                          <td data-label="Game">{g.name}</td>
                          <td data-label="Provider" className="text-muted">
                            {g.provider}
                          </td>
                          <td data-label="Hold" className="num">
                            {g.holdBp === null ? '—' : `${(g.holdBp / 100).toFixed(1)}%`}
                          </td>
                          <td data-label="GGR" className="num">
                            {formatPesosCompact(BigInt(g.ggr))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              <Panel
                title="Top players"
                actions={
                  <Button variant="ghost" onClick={() => navigate('/players')}>
                    All players
                  </Button>
                }
              >
                <div className="table-scroll">
                  <table className="table responsive">
                    <caption className="sr-only">Top players by net deposits</caption>
                    <thead>
                      <tr>
                        <th scope="col">Player</th>
                        <th scope="col">Tier</th>
                        <th scope="col" className="num">
                          Net deposits
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPlayers.map((p) => (
                        <tr
                          key={p.playerId}
                          // A clickable row is a real control. DataTable makes its
                          // own rows reachable and operable from the keyboard; these
                          // hand-rolled tables have to do the same, or the only way
                          // to open a top player is with a mouse.
                          tabIndex={0}
                          role="button"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/players/${p.playerId}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/players/${p.playerId}`);
                            }
                          }}
                        >
                          <td data-label="Player">
                            {p.name}
                            <div className="text-subtle" style={{ fontSize: '11px' }}>
                              {p.ref} · {p.brandName}
                            </div>
                          </td>
                          <td data-label="Tier">
                            <Tag tone={p.tier.startsWith('VIP') ? 'accent' : 'neutral'}>
                              {p.tier.replace('_', ' ')}
                            </Tag>
                          </td>
                          <td data-label="Net deposits" className="num">
                            {formatPesos(BigInt(p.netDeposits))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>

            {/* Named gaps. Never a fabricated zero. */}
            {unavailable.length > 0 && (
              <Panel title="Not yet available">
                <div className="stack">
                  {unavailable.map((u) => (
                    <div key={u.section} className="notice notice-quiet">
                      <Icon name="info" />
                      <div>
                        <strong>{u.section}</strong> — {u.reason}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}
          </>
        )}
      </div>
    </>
  );
}

function rangeLabel(range: DateRange): string {
  return (
    {
      TODAY: 'today',
      SEVEN_DAYS: '7 days',
      THIRTY_DAYS: '30 days',
      QTD: 'quarter to date',
      CUSTOM: 'custom window',
    } as const
  )[range];
}

/**
 * ── The action-queue band. The one place saturation carries presence. ──
 *
 * Its own component, with no hooks, so a test can draw it for every role and call what each control
 * would do. Every count is live, none is windowed by the range control, and every one is narrowed
 * by the brand chosen in the header — and so is the screen each pill opens: Player funds, Identity
 * checks, Risk and Support's ticket queue all follow that brand.
 *
 * EACH PILL, AND "WORK THE QUEUE", OPENS ITS QUEUE'S SCREEN ONLY FOR A READER WHO MAY OPEN IT — who
 * holds the key that screen's sidebar entry needs (queue-links.ts reads it from the sidebar's
 * own table). Anyone else gets the same figures with no door: the pill is not a button, and a line
 * under it names the key and says their role does not hold it; "Work the queue", which opens the
 * payout queue, is not drawn, and the payout pill beside it names the key. Until this was decided
 * per reader, the Risk pill opened a refusal for every Finance Lead, Auditor and Support Agent, and
 * the payout pill and "Work the queue" did for every Support Agent.
 *
 * THE KYC REVIEW PILL counts identity checks waiting for a person, by the identity queue's own
 * definition, in the same reader scope and for the same header brand, and opens that queue on its
 * waiting list — so the count is the number of cases that list holds, with a brand chosen or not,
 * of which the list shows the oldest 200. (The other pills count by definitions of their own.)
 * Until 23 October 2026 it counted the older process's PENDING submissions and opened the roster
 * filtered to Pending, a list of accounts nobody could work a case from.
 */
export function ActionQueueBand({
  queue,
  brandId,
  brands,
  allowed,
  role,
  onOpen,
}: {
  queue: ActionQueue;
  brandId: string | null;
  brands: readonly Pick<Brand, 'id' | 'name'>[];
  allowed: (key: Permission) => boolean;
  /** The reader's role, named in the sentence a pill shows in place of a link. */
  role: string;
  onOpen: (to: string) => void;
}) {
  const openQueue =
    queue.withdrawalsAwaitingChecker.count +
    queue.kycReview.count +
    queue.riskAlerts.count +
    queue.supportTickets.count;
  /* A pill's door for this reader: a click that opens its queue's screen, or none and the line
     naming the key it needs. */
  const door = (kind: WorkKind) => {
    const to = queueOpens(kind, allowed);
    return to === null
      ? { onClick: undefined, note: queueWithheld(kind, role) }
      : { onClick: () => onOpen(to), note: undefined };
  };
  const payouts = queueOpens('WITHDRAWAL', allowed);

  return (
    <section className="queue-band" aria-labelledby="queue-heading">
      <div className="row" style={{ marginBottom: 'var(--space-3)' }}>
        <span id="queue-heading" className="queue-heading">
          Action queue
        </span>
        <span className="text-subtle" style={{ fontSize: '12px' }}>
          {openQueue} open · {queue.withdrawalsAwaitingChecker.breachingSla} breaching SLA
        </span>
        {payouts !== null && (
          <Button variant="primary" className="header-spacer" onClick={() => onOpen(payouts)}>
            Work the queue
            <Icon name="arrow-right" />
          </Button>
        )}
      </div>

      <div className="queue-grid">
        <QueuePill
          icon="arrow-up-right"
          label="Withdrawals · checker"
          count={queue.withdrawalsAwaitingChecker.count}
          aside={formatPesosCompact(BigInt(queue.withdrawalsAwaitingChecker.value))}
          meta={
            queue.withdrawalsAwaitingChecker.oldestMinutes === null
              ? 'nothing waiting'
              : `oldest ${formatWait(queue.withdrawalsAwaitingChecker.oldestMinutes)} · ${queue.withdrawalsAwaitingChecker.breachingSla} over SLA`
          }
          attention={queue.withdrawalsAwaitingChecker.breachingSla > 0}
          {...door('WITHDRAWAL')}
        />
        <QueuePill
          icon="identification-card"
          label="KYC review"
          count={queue.kycReview.count}
          /* Waited since the check was handed to a person, as the queue counts it. */
          meta={
            queue.kycReview.oldestMinutes === null
              ? 'nothing waiting'
              : `oldest ${formatWait(queue.kycReview.oldestMinutes)}`
          }
          {...door('KYC')}
        />
        <QueuePill
          icon="shield-warning"
          label="Risk alerts"
          count={queue.riskAlerts.count}
          aside={`${queue.riskAlerts.critical} critical`}
          meta="velocity · bonus abuse"
          attention={queue.riskAlerts.critical > 0}
          {...door('RISK')}
        />
        <QueuePill
          icon="lifebuoy"
          label="Support tickets"
          count={queue.supportTickets.count}
          aside={`${queue.supportTickets.escalated} escalated`}
          /*
           * THE SCOPE THIS NUMBER IS ACTUALLY IN.
           *
           * It said "open across all brands" as a fixed string while the
           * query behind it has always been brand-scoped. With Bingo Barrio
           * selected the tile read "1 · open across all brands"; 65 were open
           * across all brands. The number was right and the words under it
           * were not, which is worse than either being wrong alone — an
           * operator who believed the caption would have read the platform as
           * quiet when sixty-four tickets were waiting on other brands.
           *
           * Derived from the filter rather than written down, so it cannot go
           * stale the next time the query changes.
           */
          meta={
            brandId
              ? `open in ${brands.find((b) => b.id === brandId)?.name ?? 'this brand'}`
              : brands.length === 1
                ? `open in ${brands[0]!.name}`
                : `open across ${allBrandsPhrase(brands)}`
          }
          {...door('TICKET')}
        />
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   The three dashboards.

   The export ships three, and they answer different questions rather than being
   restyles of one: Live Ops is "what needs doing right now", Period BI is "how
   did we do", and Split is the operator's default — the queue and the headline
   figures on one screen. The choice is remembered per browser, because an
   operator who works the queue wants the queue every morning.
   ═══════════════════════════════════════════════════════════════════════ */

const VIEWS = [
  { key: 'split', label: 'Split', icon: 'columns' },
  { key: 'live', label: 'Live ops', icon: 'pulse' },
  { key: 'bi', label: 'Period BI', icon: 'chart-line' },
] as const;

type DashboardView = (typeof VIEWS)[number]['key'];

const VIEW_KEY = 'dayon-back-office-dashboard-view';

function readView(): DashboardView {
  try {
    const stored = localStorage.getItem(VIEW_KEY);
    return VIEWS.some((v) => v.key === stored) ? (stored as DashboardView) : 'split';
  } catch {
    return 'split';
  }
}

export function Dashboard() {
  const { allowed } = useSession();
  /*
   * players.view, for all three views: every figure and the queue on each of them comes from a
   * route that needs it. One gate here covers them, because they are children — a view that is
   * never mounted registers no effect and reads nothing.
   */
  const canReadPlayers = allowed('players.view');
  /* Reports' own key: the landing below sends a reader there only if Reports will open for them. */
  const canReadReports = allowed('audit.export');
  /*
   * POP is the browser putting the reader here — the console opening on this address, a reload,
   * back or forward. Anything else is a link inside the console.
   */
  const opened = useNavigationType() === NavigationType.Pop;
  const [view, setView] = useState<DashboardView>('split');

  // Read after mount rather than during render: the value comes from storage,
  // which can throw, and reading it in the initialiser would make the first
  // paint depend on it.
  useEffect(() => setView(readView()), []);

  const choose = (next: DashboardView) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      /* a remembered view is not worth breaking the page over */
    }
  };

  /*
   * THE CONSOLE OPENS HERE, AND A READER WITHOUT players.view LANDS ON REPORTS.
   *
   * This route is the landing for everyone: the address the sign-in screen is on, and the one a
   * new tab opens. For the PAGCOR Regulator (item 22), which holds no player key, a refusal is no
   * place to start, and Reports is what the role is for. So the console opening here sends them
   * there — replacing the address, so Back does not return them to a screen they cannot use.
   * Reached any other way from inside the console, the dashboard says instead why it is refused,
   * in the words every refusing module uses — and so it does for a reader who could not open
   * Reports either, rather than sending them from one locked door to another. (The console's own
   * home link, the brand mark, already takes such a reader to the first module they can open:
   * `homePath` in @dayon/rbac.)
   *
   * Decided here rather than in the route table, whose `<Route path=… element={<X />} />` lines
   * are read by permission-gates.test.ts and nav.test.ts.
   */
  if (!canReadPlayers && canReadReports && opened) {
    return <Navigate to="/reports" replace />;
  }
  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Dashboard">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  return (
    <>
      {/*
        THE WRAPPER STAYS, AND IT IS NOT DECORATION.

        This screen is full-bleed. Nothing above the views supplies a gutter,
        the queue band below brings its own, and the figures further down sit in
        a body that is capped to the readable content width. A header dropped
        straight into the fragment would start at the window edge, out of line
        with every heading under it. This div is the one that already carries
        the band's horizontal gutter, which is why the title, the switcher and
        the queue pills below them share one left edge.

        The class name stays as it is as well, because the browser walk finds
        the view switcher through it (`.dashboard-views label.seg-opt`) to tell
        it apart from the range control inside the split view, which is built
        from the same segmented-control markup. Renaming it from here would
        leave that walk clicking the wrong control and still passing.

        What did come off the div is `row`. `.screen-header` is its own flex row
        and sets the title against the switcher itself; left as a flex ITEM of
        `.row` it would have shrunk to its content, and the switcher would have
        crowded the purpose sentence instead of holding the right edge.
      */}
      <div className="dashboard-views">
        {/*
          THE SENTENCE SITS ABOVE THE SWITCHER, NOT INSIDE ANY ONE VIEW.

          The three views answer three different questions — the banner comment
          above `VIEWS` says which — and each one reads its own figures. A
          purpose written inside them would have to be written three times, and
          three copies drift the first time somebody edits one. Up here it is
          written once, and it is on screen whichever view is showing, including
          while a view is still loading and after one has failed to load, which
          is the moment an operator who was sent here by a colleague most needs
          telling where they have landed.

          AND IT DELIBERATELY CARRIES NO `meta`. The derived caption this screen
          has — the open-item total and the count breaching SLA — is summed from
          the payload the split view fetches, so it exists only inside that view
          and only after a read that came back. Lifting it up here would put a
          figure above the switcher with nothing to refresh it: it would go stale
          the moment somebody moved to Live ops or Period BI, and it would still
          be sitting there after a failed read looking like a count that had just
          been taken. It stays on the queue band it describes, beside the button
          that works the queue.
        */}
        <ScreenHeader
          title="Dashboard"
          purpose="Where an operator starts a shift — what needs working right now, and how the business is doing, in whichever of the three views they prefer."
          actions={
            <div className="seg" role="radiogroup" aria-label="Dashboard view">
              {VIEWS.map((v) => (
                <label className="seg-opt" key={v.key} title={v.label}>
                  <input
                    type="radio"
                    name="dashboard-view"
                    checked={view === v.key}
                    onChange={() => choose(v.key)}
                  />
                  <Icon name={v.icon} />
                  <span className="dashboard-view-label">{v.label}</span>
                </label>
              ))}
            </div>
          }
        />
      </div>

      {view === 'split' && <SplitDashboard />}
      {view === 'live' && <LiveOps />}
      {view === 'bi' && <PeriodBI />}
    </>
  );
}
